import { useEffect, useRef, useState } from "react";
import { ArrowDown, ChevronDown, ChevronUp } from "lucide-react";
import { FOUNDER_LETTER, IMAGES, scrollToId } from "../../lib/content";
import { Reveal } from "./Reveal";

const DURATION = 500;
const toggleCls = "inline-flex items-center gap-1.5 font-sans text-base font-semibold text-[#C85A32] hover:text-[#B24E2A] transition-colors duration-200";
const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const PULL_QUOTES = ["But our children will only be little once.", "She needs a father who is there."];

const Para = ({ text, i }) => {
  const id = `founder-letter-paragraph-${i + 1}`;
  if (text.startsWith('"'))
    return <p data-testid={id} className="border-l-2 border-[#6E8576]/40 pl-5 italic text-[#536B5B]">{text}</p>;
  if (PULL_QUOTES.includes(text))
    return <p data-testid={id} className="py-3 text-2xl sm:text-3xl leading-snug italic text-[#C85A32]">{text}</p>;
  return <p data-testid={id}>{text}</p>;
};

const LetterBody = () => {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [style, setStyle] = useState({});
  const pendingScroll = useRef(false);

  const expand = () => {
    setStyle({ maxHeight: ref.current.scrollHeight });
    setExpanded(true);
    setTimeout(() => setStyle({ maxHeight: "none" }), reducedMotion() ? 0 : DURATION);
  };

  const collapse = () => {
    setStyle({ maxHeight: ref.current.scrollHeight });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      pendingScroll.current = true;
      setStyle({});
      setExpanded(false);
    }));
  };

  useEffect(() => {
    if (expanded || !pendingScroll.current) return;
    pendingScroll.current = false;
    const reduced = reducedMotion();
    const t = setTimeout(() => document.getElementById("founder")?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" }), reduced ? 0 : DURATION);
    return () => clearTimeout(t);
  }, [expanded]);

  return (
    <>
      <div className="relative mt-8">
        <div
          ref={ref}
          id="founder-letter-body"
          data-testid="founder-letter-body"
          data-expanded={expanded}
          style={style}
          className="max-h-[8rem] sm:max-h-[9rem] overflow-hidden [overflow-anchor:none] transition-[max-height] duration-500 ease-in-out motion-reduce:transition-none"
        >
          <div className="space-y-5 sm:space-y-6 font-serif text-[1.075rem] sm:text-xl leading-[1.8] text-[#2C221E]">
            {FOUNDER_LETTER.paragraphs.map((text, i) => <Para key={i} text={text} i={i} />)}
          </div>
          {expanded && (
            <button type="button" onClick={collapse} aria-expanded="true" aria-controls="founder-letter-body" data-testid="founder-read-less-button" className={`${toggleCls} mt-8`}>
              Read less <ChevronUp className="h-4 w-4" />
            </button>
          )}
        </div>
        <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-[#EAEFEA]/0 to-[#EAEFEA] transition-opacity duration-500 motion-reduce:transition-none ${expanded ? "opacity-0" : "opacity-100"}`} />
      </div>
      {!expanded && (
        <button type="button" onClick={expand} aria-expanded="false" aria-controls="founder-letter-body" data-testid="founder-read-more-button" className={`${toggleCls} mt-5`}>
          Read Kevin's full story <ChevronDown className="h-4 w-4" />
        </button>
      )}
    </>
  );
};

export const KAvatar = () => (
  <svg data-testid="founder-avatar" width="72" height="72" viewBox="0 0 72 72" role="img" aria-label="Kevin's initial K">
    <circle cx="36" cy="36" r="34" fill="#C85A32" />
    <circle cx="36" cy="36" r="29" fill="none" stroke="#F2C98F" strokeWidth="1.5" strokeDasharray="5 4" />
    <path d="M14 50 C 24 58, 48 58, 58 50" stroke="#D9A05B" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.8" />
    <text x="36" y="46" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontStyle="italic" fontSize="32" fill="#FDFBF7">K</text>
  </svg>
);

export const FounderNote = () => (
  <section id="founder" data-testid="founder-section" className="section bg-[#FDFBF7]">
    <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16 grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
      <Reveal className="lg:col-span-4 lg:sticky lg:top-16">
        <p className="eyebrow">Why Woven exists</p>
        <h2 data-testid="founder-heading" className="section-title mt-4">A note from our founder</h2>
        <div className="photo-frame mt-10 aspect-[4/5] hidden sm:block">
          <img src={IMAGES.dadShoulders.src} alt={IMAGES.dadShoulders.alt} loading="lazy" className="h-full w-full object-cover" />
        </div>
      </Reveal>

      <Reveal delay={150} className="lg:col-span-8">
        <article data-testid="founder-letter" className="relative rounded-[2rem] border border-[#6E8576]/20 bg-[#EAEFEA] px-6 py-10 sm:p-12 lg:p-16 shadow-[0_10px_30px_rgba(44,34,30,0.06)]">
          <span aria-hidden="true" className="absolute -top-6 left-6 sm:left-8 font-serif text-8xl leading-none text-[#6E8576]/40">“</span>
          <div className="max-w-[65ch]">
            <h3 data-testid="founder-letter-title" className="font-serif text-2xl sm:text-3xl leading-tight text-[#2C221E]">
              {FOUNDER_LETTER.title}
            </h3>
            <LetterBody />
            <div className="mt-12 flex items-center gap-5 border-t border-[#6E8576]/25 pt-8">
              <KAvatar />
              <p data-testid="founder-signoff" className="font-serif italic text-xl text-[#536B5B]">{FOUNDER_LETTER.signoff}</p>
            </div>
            <button data-testid="founder-join-waitlist-button" onClick={() => scrollToId("waitlist")} className="btn-soft group mt-10">
              Join the Waitlist
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </div>
        </article>
      </Reveal>
    </div>
  </section>
);
